import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Tooltip, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faInbox,
  faClock,
  faCalendar,
  faCalendarAlt,
  faCalendarCheck,
  faTruck,
  faCheck,
  faTimes,
  faBoxOpen,
  faCheckCircle,
  faHourglassHalf,
  faTimesCircle,
  faCopy,
  faMapMarkerAlt,
  faExclamationTriangle,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import AllocationConfirmModal from "../Modals/AllocationConfirmModal";
import ActionButton from "../ui/ActionButton";
import "../../style/ReceiversTablePanel.css";

// get status info for a request
// priority: higher = shown first when sorted descending
// Pending (4) > Allocated-pending (3) > Allocated-scheduled (2) > Declined (1) > Delivered (0)
const getRequestStatus = (request) => {
  if (!request.active) {
    return { text: "Declined", color: "default", priority: 1, tooltip: null };
  }
  if (request.date_delivered) {
    return { text: "Delivered", color: "primary", priority: 0, tooltip: null };
  }
  if (request.date_allocated) {
    if (request.pickup_confirmed) {
      return {
        text: "Allocated",
        color: "teal",
        priority: 2,
        tooltip: "Pickup Scheduled by Receiver",
      };
    }
    return {
      text: "Allocated",
      color: "teal",
      priority: 3,
      tooltip: "Pickup Notice sent to Receiver",
    };
  }
  return { text: "Pending", color: "warning", priority: 4, tooltip: null };
};

// parse date string as local date
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateString);
};

// format date for display
const formatDisplayDate = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// format pickup time
const formatPickupTime = (timeValue) => {
  if (!timeValue) return null;
  try {
    const timeStr = String(timeValue);
    if (timeStr.includes("-")) {
      const [start, end] = timeStr.split("-").map((t) => t.trim());
      const formatPart = (t) => {
        const parts = t.split(":");
        const h = parseInt(parts[0]);
        const m = parts[1] || "00";
        return `${h}:${m.substring(0, 2)}`;
      };
      return `${formatPart(start)} – ${formatPart(end)}`;
    }
    const parts = timeStr.split(":");
    const h = parseInt(parts[0]);
    const m = parts[1] || "00";
    return `${h}:${m.substring(0, 2)}`;
  } catch {
    return String(timeValue);
  }
};

// format scheduled date to match listings table (YYYY-MM-DD)
const formatScheduledDateShort = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = parseLocalDate(dateString);
    if (!date || isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return dateString;
  }
};

// format time ago from UTC timestamp
const getTimeAgo = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;

    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return `${diffMonths}mo ago`;
  } catch {
    return null;
  }
};

// format full date/time for tooltip
const formatFullDateTime = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

// calculate allocation segments for circle chart
const calculateSegments = (listing, requests) => {
  const total = listing?.quantity?.parsedValue || listing?.quantity || 0;
  if (total === 0) return { delivered: 0, allocated: 0, available: 0 };

  let deliveredQty = 0;
  let allocatedQty = 0;

  (requests || []).forEach((req) => {
    if (!req.active) return; // skip declined
    // handle quantity as object or number
    const qty = req.quantity?.parsedValue || req.quantity || 0;

    if (req.date_delivered) {
      deliveredQty += qty;
    } else if (req.date_allocated) {
      // includes both Allocated and Awaiting Pickup
      allocatedQty += qty;
    }
  });

  const availableQty =
    listing?.remaining_qty?.parsedValue || listing?.remaining_qty || 0;

  // convert to percentages
  return {
    delivered: Math.round((deliveredQty / total) * 100),
    allocated: Math.round((allocatedQty / total) * 100),
    available: Math.round((availableQty / total) * 100),
    deliveredQty,
    allocatedQty,
    availableQty,
  };
};

const ReceiversTablePanel = ({ listing, darkMode, onAllocate, onDecline }) => {
  const { t } = useTranslation();
  const [allocatingIds, setAllocatingIds] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState(null);
  const [customQuantities, setCustomQuantities] = useState({});
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [copiedListingId, setCopiedListingId] = useState(false);

  // copy address to clipboard
  const handleCopyAddress = useCallback((e, rowId, address) => {
    e.stopPropagation();
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopiedRowId(rowId);
    setTimeout(() => setCopiedRowId(null), 2000);
  }, []);

  // open address in Google Maps
  const handleOpenInMaps = useCallback((e, address) => {
    e.stopPropagation();
    if (!address) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // copy listing ID to clipboard
  const handleCopyListingId = useCallback((e) => {
    e.stopPropagation();
    if (!listing?.id) return;
    navigator.clipboard.writeText(listing.id);
    setCopiedListingId(true);
    setTimeout(() => setCopiedListingId(false), 2000);
  }, [listing?.id]);

  // handle custom quantity change
  const handleQuantityChange = useCallback((requestId, value) => {
    const numValue = parseFloat(value) || 0;
    setCustomQuantities((prev) => ({
      ...prev,
      [requestId]: numValue,
    }));
  }, []);

  // check if pickup date is close to or past the listing deadline
  const getPickupDateStatus = useCallback((pickupDateString) => {
    if (!pickupDateString) return "normal";

    const listingDeadline = listing?.latest_pickup || listing?.pickup_deadline;
    if (!listingDeadline) return "normal";

    const pickupDate = new Date(pickupDateString);
    const deadline = new Date(listingDeadline);
    const now = new Date();

    // normalize to date only (ignore time)
    const pickupDateOnly = new Date(
      pickupDate.getFullYear(),
      pickupDate.getMonth(),
      pickupDate.getDate()
    );
    const deadlineOnly = new Date(
      deadline.getFullYear(),
      deadline.getMonth(),
      deadline.getDate()
    );

    // if pickup date is after deadline
    if (pickupDateOnly > deadlineOnly) return "past";

    // if pickup date is on the deadline
    if (pickupDateOnly.getTime() === deadlineOnly.getTime())
      return "on-deadline";

    // If pickup date is within 2 days of deadline
    const diffDays = Math.ceil(
      (deadlineOnly - pickupDateOnly) / (1000 * 60 * 60 * 24)
    );
    if (diffDays <= 2) return "soon";

    // if the pickup date itself is in the past
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const daysUntilPickup = Math.ceil(
      (pickupDateOnly - todayOnly) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilPickup < 0) return "past";
    if (daysUntilPickup <= 2) return "soon";

    return "normal";
  }, [listing?.latest_pickup, listing?.pickup_deadline]);

  // dataGrid styles
  const dataGridStyles = useMemo(
    () => ({
      border: "none",
      fontFamily: '"Lato", sans-serif',
      fontSize: "0.875rem",
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: darkMode ? "rgba(30, 30, 30, 1)" : "#f5f5f5",
        borderBottom: darkMode
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid #e0e0e0",
        minHeight: "48px !important",
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: darkMode ? "rgba(30, 30, 30, 1)" : "#f5f5f5",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: darkMode ? "rgba(255, 255, 255, 0.7)" : "#666666",
      },
      "& .MuiDataGrid-row:nth-of-type(odd)": {
        backgroundColor: darkMode ? "transparent" : "#ffffff",
      },
      "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.02)" : "#f8f8f8",
      },
      "& .MuiDataGrid-row:hover": {
        backgroundColor: darkMode
          ? "rgba(102, 157, 46, 0.1) !important"
          : "rgba(102, 157, 46, 0.08) !important",
      },
      "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
        borderBottom: darkMode
          ? "1px solid rgba(255, 255, 255, 0.04)"
          : "1px solid #f0f0f0",
      },
      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
      },
      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
        {
          outline: "none",
        },
    }),
    [darkMode]
  );

  // show confirmation modal for allocation
  const showAllocateConfirm = (requestId, quantity, companyName) => {
    setConfirmModal({ type: "allocate", requestId, quantity, companyName });
  };

  // show confirmation modal for decline
  const showDeclineConfirm = (requestId, companyName) => {
    setConfirmModal({ type: "decline", requestId, companyName });
  };

  // close modal
  const closeModal = useCallback(() => {
    setConfirmModal(null);
  }, []);

  // handle confirmed allocation
  const handleAllocate = useCallback(async (requestId, quantity) => {
    if (allocatingIds.has(requestId)) return;
    closeModal();

    setAllocatingIds((prev) => new Set(prev).add(requestId));

    try {
      await onAllocate(requestId, quantity);
    } finally {
      setAllocatingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
      // clear custom quantity after allocation
      setCustomQuantities((prev) => {
        const newObj = { ...prev };
        delete newObj[requestId];
        return newObj;
      });
    }
  }, [allocatingIds, onAllocate, closeModal]);

  // handle confirmed decline
  const handleDecline = useCallback(async (requestId) => {
    if (allocatingIds.has(requestId)) return;
    closeModal();

    setAllocatingIds((prev) => new Set(prev).add(requestId));

    try {
      await onDecline(requestId);
    } finally {
      setAllocatingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  }, [allocatingIds, onDecline, closeModal]);

  const requests = listing?.requests || [];

  // columns for the receivers table
  const columns = useMemo(() => [
    {
      field: "receiver_company",
      headerName: t("Receiver"),
      flex: 1.5,
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      valueGetter: (value, row) => row.receiver_company || row.client_id,
      renderCell: (params) => (
        <Box className="receiver-cell">
          <div className="receiver-avatar">
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <span className="receiver-name">
            {params.row.receiver_company || params.row.client_id}
          </span>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("Status"),
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      sortComparator: (v1, v2, param1, param2) => {
        const row1 = param1.api.getRow(param1.id);
        const row2 = param2.api.getRow(param2.id);
        const status1 = getRequestStatus(row1);
        const status2 = getRequestStatus(row2);
        return status1.priority - status2.priority;
      },
      renderCell: (params) => {
        const status = getRequestStatus(params.row);
        const isScheduled =
          params.row.pickup_confirmed && params.row.date_allocated;
        const isAllocatedStatus = status.text === "Allocated";

        // icon selection
        const icon =
          status.text === "Delivered"
            ? faCheckCircle
            : isAllocatedStatus && isScheduled
            ? faCalendarCheck
            : isAllocatedStatus
            ? faCalendar
            : status.text === "Pending"
            ? faHourglassHalf
            : faTimesCircle;

        // icon color for Allocated status
        const iconColor = isAllocatedStatus
          ? "#000000" // black for both states
          : undefined;

        // forest green styling for Allocated badge
        const allocatedSx = isAllocatedStatus
          ? {
              backgroundColor: "#4caf50",
              color: "#000",
              "& .MuiChip-icon": { color: iconColor },
            }
          : {};

        const chip = (
          <Chip
            icon={
              <FontAwesomeIcon
                icon={icon}
                style={
                  iconColor
                    ? { color: iconColor, fontSize: "0.65rem" }
                    : { fontSize: "0.65rem" }
                }
              />
            }
            label={t(status.text)}
            color={isAllocatedStatus ? "default" : status.color}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.7rem",
              "& .MuiChip-icon": { fontSize: "0.65rem" },
              ...allocatedSx,
            }}
          />
        );

        return status.tooltip ? (
          <Tooltip title={t(status.tooltip)} placement="top">
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      field: "quantity_range",
      headerName: t("Qty Range (lbs)"),
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) => `${row.min_qty}-${row.max_qty}`,
      renderCell: (params) => (
        <span className="qty-range">
          {params.row.min_qty?.toLocaleString()} –{" "}
          {params.row.max_qty?.toLocaleString()}
        </span>
      ),
    },
    {
      field: "receiver_ghg_rating",
      headerName: t("GHG Rating"),
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const rating = params.value;
        if (!rating) return "—";
        return <span className="ghg-rating">{Number(rating).toFixed(1)}</span>;
      },
    },
    {
      field: "receiver_address",
      headerName: t("Address"),
      flex: 1.5,
      minWidth: 180,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const address = params.value;
        const rowId = params.row.id;
        if (!address) return "—";

        const isCopied = copiedRowId === rowId;

        return (
          <Box className="address-cell-container">
            <Tooltip title={address} placement="top">
              <span className="address-text">{address}</span>
            </Tooltip>
            <Box className="address-actions">
              <Tooltip
                title={isCopied ? t("Copied!") : t("Copy address")}
                placement="top"
              >
                <Box
                  className="address-icon-btn"
                  onClick={(e) => handleCopyAddress(e, rowId, address)}
                >
                  <FontAwesomeIcon
                    icon={isCopied ? faCheck : faCopy}
                    className={`address-icon ${isCopied ? "copied" : ""}`}
                  />
                </Box>
              </Tooltip>
              <Tooltip title={t("Open in Google Maps")} placement="top">
                <Box
                  className="address-icon-btn"
                  onClick={(e) => handleOpenInMaps(e, address)}
                >
                  <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    className="address-icon map-icon"
                  />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "scheduled_date",
      headerName: t("Pickup Date"),
      flex: 0.9,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      valueGetter: (value) => (value ? parseLocalDate(value) : null),
      renderCell: (params) => {
        const dateStr = params.row.scheduled_date;
        if (!dateStr) return "—";

        const status = getPickupDateStatus(dateStr);
        const formattedDate = formatScheduledDateShort(dateStr);

        // get icon based on status
        const getIcon = () => {
          switch (status) {
            case "past":
              return faCircleXmark;
            case "on-deadline":
              return faExclamationTriangle;
            case "soon":
              return faHourglassHalf;
            default:
              return null;
          }
        };

        // get tooltip text based on status
        const getTooltip = () => {
          switch (status) {
            case "past":
              return t("Past deadline");
            case "on-deadline":
              return t("On deadline");
            case "soon":
              return t("Close to deadline");
            default:
              return "";
          }
        };

        const icon = getIcon();

        return (
          <Tooltip title={getTooltip()} placement="top">
            <Box className={`pickup-date-cell ${status}`}>
              {icon && (
                <FontAwesomeIcon icon={icon} className="pickup-date-icon" />
              )}
              {formattedDate}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "time",
      headerName: t("Pickup Time"),
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) =>
        row.time || row.scheduled_time || row.pickup_time,
      renderCell: (params) => {
        const timeValue =
          params.row.time ||
          params.row.scheduled_time ||
          params.row.pickup_time;
        return formatPickupTime(timeValue) || "—";
      },
    },
    {
      field: "requested",
      headerName: t("Requested"),
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) =>
        row.created_at || row.date_created || row.request_date,
      sortComparator: (v1, v2) => {
        const date1 = v1 ? new Date(v1).getTime() : 0;
        const date2 = v2 ? new Date(v2).getTime() : 0;
        return date1 - date2;
      },
      renderCell: (params) => {
        const dateStr =
          params.row.created_at ||
          params.row.date_created ||
          params.row.request_date;
        const timeAgo = getTimeAgo(dateStr);
        const fullDate = formatFullDateTime(dateStr);

        if (!timeAgo) return "—";

        return (
          <Tooltip title={fullDate || ""} placement="top">
            <span className="time-ago">{timeAgo}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "revenue",
      headerName: t("Revenue"),
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) => {
        if (row.date_delivered && row.allocated_cost) {
          return row.allocated_cost;
        }
        return null;
      },
      renderCell: (params) => {
        const request = params.row;
        if (request.date_delivered && request.allocated_cost) {
          return (
            <span
              className="revenue-value"
              style={{ color: "#4caf50", fontWeight: 500 }}
            >
              ${request.allocated_cost.toFixed(2)}
            </span>
          );
        }
        return "—";
      },
    },
    {
      field: "actions",
      headerName: t("Actions"),
      flex: 2,
      minWidth: 280,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const request = params.row;
        const status = getRequestStatus(request);
        const isPending = status.text === "Pending";
        const isAllocated =
          status.text === "Allocated" && !request.pickup_confirmed;
        const isScheduled =
          status.text === "Allocated" && request.pickup_confirmed;
        const isProcessing = allocatingIds.has(request.id);

        const maxAllocatable = Math.min(request.max_qty, listing.remaining_qty);

        const customQty = customQuantities[request.id] || "";
        const isCustomValid =
          customQty &&
          customQty >= request.min_qty &&
          customQty <= maxAllocatable;

        if (isAllocated || isScheduled) {
          return (
            <Chip
              icon={
                <FontAwesomeIcon
                  icon={isScheduled ? faCalendarCheck : faCalendar}
                  style={{ color: "#000000", fontSize: "0.65rem" }}
                />
              }
              label={`${request.quantity?.toLocaleString()} lbs`}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.7rem",
                backgroundColor: "#4caf50",
                color: "#000",
                "& .MuiChip-icon": { fontSize: "0.65rem" },
              }}
            />
          );
        }

        if (!request.active) {
          return (
            <Chip
              label={t("Declined")}
              size="small"
              sx={{
                backgroundColor: "rgba(107, 114, 128, 0.15)",
                color: "#6b7280",
                fontWeight: 500,
                fontSize: "0.7rem",
              }}
            />
          );
        }

        if (isPending) {
          return (
            <Box className="actions-cell" onClick={(e) => e.stopPropagation()}>
              <ActionButton
                variant="default"
                size="small"
                tooltip={`${t("Allocate Min")}: ${request.min_qty} lbs`}
                onClick={() =>
                  showAllocateConfirm(
                    request.id,
                    request.min_qty,
                    request.receiver_company || request.client_id
                  )
                }
                disabled={
                  isProcessing || request.min_qty > listing.remaining_qty
                }
              >
                Min
              </ActionButton>
              <ActionButton
                variant="secondary"
                size="small"
                tooltip={`${t("Allocate Max")}: ${maxAllocatable} lbs`}
                onClick={() =>
                  showAllocateConfirm(
                    request.id,
                    maxAllocatable,
                    request.receiver_company || request.client_id
                  )
                }
                disabled={isProcessing || maxAllocatable <= 0}
              >
                Max
              </ActionButton>
              <TextField
                type="number"
                size="small"
                placeholder={t("Custom")}
                value={customQty}
                onChange={(e) =>
                  handleQuantityChange(request.id, e.target.value)
                }
                onClick={(e) => e.stopPropagation()}
                inputProps={{
                  min: request.min_qty,
                  max: maxAllocatable,
                  step: 1,
                }}
                className="custom-qty-input"
                sx={{
                  width: "70px",
                  "& .MuiInputBase-input": {
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                    textAlign: "center",
                  },
                  "& .MuiOutlinedInput-root": {
                    height: "28px",
                  },
                }}
              />
              <ActionButton
                variant="secondary"
                size="small"
                iconOnly
                icon={faCheck}
                tooltip={
                  isCustomValid
                    ? `${t("Allocate")}: ${customQty} lbs`
                    : t("Enter valid quantity")
                }
                onClick={() =>
                  showAllocateConfirm(
                    request.id,
                    customQty,
                    request.receiver_company || request.client_id
                  )
                }
                disabled={isProcessing || !isCustomValid}
              />
              <ActionButton
                variant="danger"
                size="small"
                iconOnly
                icon={faTimes}
                tooltip={t("Decline request")}
                onClick={() =>
                  showDeclineConfirm(
                    request.id,
                    request.receiver_company || request.client_id
                  )
                }
                disabled={isProcessing}
              />
            </Box>
          );
        }

        // Delivered
        if (status.text === "Delivered") {
          return (
            <Chip
              icon={
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#000000", fontSize: "0.65rem" }}
                />
              }
              label={`${request.quantity?.toLocaleString()} lbs`}
              color="primary"
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { fontSize: "0.65rem" },
              }}
            />
          );
        }

        return (
          <Chip
            label={t(status.text)}
            color={status.color}
            size="small"
            sx={{ fontWeight: 500, fontSize: "0.7rem" }}
          />
        );
      },
    },
  ], [t, listing, allocatingIds, copiedRowId, customQuantities, handleAllocate, handleDecline, handleCopyAddress, handleOpenInMaps, handleCopyListingId, handleQuantityChange, copiedListingId, getPickupDateStatus]);

  // empty state - no listing selected
  if (!listing) {
    return (
      <Box className={`receivers-table-panel empty ${darkMode ? "dark" : ""}`}>
        <Box className="empty-state">
          <div className="empty-icon-wrapper">
            <FontAwesomeIcon icon={faBoxOpen} className="empty-icon" />
          </div>
          <h3>{t("Select a Listing")}</h3>
          <p>{t("Click on a listing to view interested receivers")}</p>
        </Box>
      </Box>
    );
  }

  // calculate segments for circle chart
  const segments = calculateSegments(listing, requests);

  // segment colors
  const SEGMENT_COLORS = {
    delivered: "#2196f3", // blue
    allocated: "#4caf50", // green
    available: "#ff9800", // orange
  };

  return (
    <Box className={`receivers-table-panel ${darkMode ? "dark" : ""}`}>
      {/* Listing Info Banner */}
      <Box className="listing-info-banner">
        <Box className="banner-left">
          {/* Circular Progress with segments */}
          <Box className="circular-progress-container">
            <svg viewBox="0 0 36 36" className="circular-progress">
              {/* Background circle */}
              <path
                className="circle-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              {/* Available segment*/}
              <path
                className="circle-segment"
                strokeDasharray={`${
                  segments.available + segments.allocated + segments.delivered
                }, 100`}
                style={{ stroke: SEGMENT_COLORS.available }}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              {/* Allocated segment (green) */}
              {segments.allocated + segments.delivered > 0 && (
                <path
                  className="circle-segment"
                  strokeDasharray={`${
                    segments.allocated + segments.delivered
                  }, 100`}
                  style={{ stroke: SEGMENT_COLORS.allocated }}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              )}
              {/* Delivered segment (blue) */}
              {segments.delivered > 0 && (
                <path
                  className="circle-segment"
                  strokeDasharray={`${segments.delivered}, 100`}
                  style={{ stroke: SEGMENT_COLORS.delivered }}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              )}
            </svg>
            <span className="progress-text">{segments.available}%</span>
          </Box>

          {/* Material & Quantity Info */}
          <Box className="banner-info">
            <span className="banner-material">{listing.material}</span>
            <span className="banner-qty">
              <strong>{listing.remaining_qty?.toLocaleString()}</strong>
              <span className="qty-separator"> / </span>
              {listing.quantity?.toLocaleString()} lbs
            </span>
            {/* Legend */}
            <Box className="segment-legend">
              <Tooltip
                title={`${segments.deliveredQty?.toLocaleString() || 0} lbs`}
              >
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: SEGMENT_COLORS.delivered }}
                  ></span>
                  {t("Delivered")}
                </span>
              </Tooltip>
              <Tooltip
                title={`${segments.allocatedQty?.toLocaleString() || 0} lbs`}
              >
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: SEGMENT_COLORS.allocated }}
                  ></span>
                  {t("Allocated")}
                </span>
              </Tooltip>
              <Tooltip
                title={`${segments.availableQty?.toLocaleString() || 0} lbs`}
              >
                <span className="legend-item">
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: SEGMENT_COLORS.available }}
                  ></span>
                  {t("Available")}
                </span>
              </Tooltip>
            </Box>
            <Tooltip
              title={copiedListingId ? t("Copied!") : t("Copy Listing ID")}
              placement="right"
            >
              <Box className="listing-id-badge" onClick={handleCopyListingId}>
                <span className="listing-id-text">{listing.id}</span>
                <FontAwesomeIcon
                  icon={copiedListingId ? faCheck : faCopy}
                  className={`listing-id-icon ${
                    copiedListingId ? "copied" : ""
                  }`}
                />
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <Box className="banner-right">
          <span className="banner-cost">${listing.cost?.toFixed(2)}/lb</span>

          <Box className="banner-meta">
            {(listing.latest_pickup || listing.pickup_deadline) && (
              <span className="meta-item">
                <FontAwesomeIcon icon={faClock} className="meta-icon" />
                {t("Deadline")}:{" "}
                {formatDisplayDate(
                  listing.latest_pickup || listing.pickup_deadline
                )}
              </span>
            )}
            {(listing.date_posted ||
              listing.date_created ||
              listing.created_at) && (
              <span className="meta-item">
                <FontAwesomeIcon icon={faCalendarAlt} className="meta-icon" />
                {t("Posted")}:{" "}
                {formatDisplayDate(
                  listing.date_posted ||
                    listing.date_created ||
                    listing.created_at
                )}
              </span>
            )}
          </Box>

          {(listing.delivery_instructions || listing.instructions) && (
            <Tooltip
              title={listing.delivery_instructions || listing.instructions}
              placement="top"
            >
              <span className="meta-item instructions">
                <FontAwesomeIcon icon={faTruck} className="meta-icon" />
                {t("Instructions")}:{" "}
                <span className="truncate">
                  {listing.delivery_instructions || listing.instructions}
                </span>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Receivers Table */}
      <Box className="receivers-table-container">
        {requests.length === 0 ? (
          <Box className="no-requests">
            <FontAwesomeIcon icon={faInbox} className="empty-icon" />
            <span>{t("No requests yet")}</span>
            <span className="hint">
              {t("Receivers can request this material from the marketplace")}
            </span>
          </Box>
        ) : (
          <DataGrid
            rows={requests}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[10, 25]}
            rowHeight={52}
            sortingOrder={["desc", "asc"]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: {
                sortModel: [{ field: "status", sort: "desc" }],
              },
            }}
            sx={dataGridStyles}
            slots={{
              noRowsOverlay: () => (
                <Box className="no-requests">
                  <FontAwesomeIcon icon={faInbox} className="empty-icon" />
                  <span>{t("No requests")}</span>
                </Box>
              ),
            }}
          />
        )}
      </Box>

      {/* Confirmation Modal */}
      <AllocationConfirmModal
        open={!!confirmModal}
        onClose={closeModal}
        onConfirm={() => {
          if (confirmModal?.type === "allocate") {
            handleAllocate(confirmModal.requestId, confirmModal.quantity);
          } else {
            handleDecline(confirmModal.requestId);
          }
        }}
        type={confirmModal?.type || "allocate"}
        quantity={confirmModal?.quantity}
        companyName={confirmModal?.companyName || ""}
        darkMode={darkMode}
        isProcessing={
          confirmModal ? allocatingIds.has(confirmModal.requestId) : false
        }
      />
    </Box>
  );
};

ReceiversTablePanel.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    material: PropTypes.string,
    quantity: PropTypes.number,
    remaining_qty: PropTypes.number,
    cost: PropTypes.number,
    latest_pickup: PropTypes.string,
    pickup_deadline: PropTypes.string,
    date_posted: PropTypes.string,
    date_created: PropTypes.string,
    created_at: PropTypes.string,
    delivery_instructions: PropTypes.string,
    instructions: PropTypes.string,
    requests: PropTypes.array,
  }),
  darkMode: PropTypes.bool,
  onAllocate: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default ReceiversTablePanel;
